import {
  QueryCache,
  QueryClient,
  type DefaultOptions,
} from "@tanstack/react-query";
import { queryCache } from "@sqlite/schema";
import { AUTH_QUERY_KEY, USER_EXISTS_IN_DB_QUERY_KEY } from "@utils/queryKeys";
import type { DurableCache } from "@utils/durableCache/createDurableCache";
import { stableStringify } from "@utils/durableCache/stableStringify";
import { eq } from "drizzle-orm";
import { meQueryKey } from "../../hooks/trpc/useMe/queryFunctions";
import type { DevicePorts, MobileDatabase } from "../../runtime/ports";
import { retryOptions } from "./queryDefaults";

/**
 * Emergency switch for the durable query cache: changing this value makes
 * every install delete all `query_cache` rows once, on its next launch.
 * Bump it (to the day's date) only when no per-query fix will do.
 *
 * App updates keep the cache, so an offline first launch after an update
 * still has its data; a native version change marks the rows stale
 * (`keepAcrossAppUpdate`). Every saved read is parsed with the query's
 * required schema, and a row that fails is deleted and treated as a miss,
 * so a changed response shape costs one fetch; a schema protects only the
 * fields it checks. A change that keeps a query's shape but changes its
 * meaning still parses, so retire its saved rows instead: a version segment
 * in a `durableQueryOptions` key factory, or for a tRPC infinite hook (whose
 * persister writes tRPC's own key) and an entity cache (keyed by entity ID)
 * a SQLite migration deleting the rows. See "Saved data across app updates"
 * in docs/contexts/mobile-runtime/reference/client-data.md.
 *
 * The wipe leaves the entity and offline tables and the auth mirror alone;
 * ending the account session clears those (`runtime/createAppRuntime.ts`).
 */
export const DURABLE_CACHE_BUSTER = "18_Sept_2025";
export const DURABLE_CACHE_BUSTER_KEY = "durable-cache-buster";
/** The app version whose session last opened the durable cache. */
export const DURABLE_CACHE_APP_VERSION_KEY = "durable-cache-app-version";

/**
 * Applies the buster, then an app update, to the saved query rows when a
 * data session opens. Best effort: a failed delete or update is logged and
 * never stops the app from booting (the web build's SQLite is a no-op).
 */
function prepareSavedQueries({
  db,
  kv,
  appVersion,
}: {
  db: MobileDatabase;
  kv: DevicePorts["kv"];
  appVersion: string;
}) {
  const stored = kv.getString(DURABLE_CACHE_BUSTER_KEY);
  // Builds before LIS-1096 stored `${appVersion}_${buster}` and wiped the
  // cache on every update. With the current buster that value is current
  // too, so the release that drops the version keeps the cache as well.
  const legacyVersion =
    stored !== DURABLE_CACHE_BUSTER &&
    stored?.endsWith(`_${DURABLE_CACHE_BUSTER}`)
      ? stored.slice(0, -DURABLE_CACHE_BUSTER.length - 1)
      : undefined;
  // An older build reinstalled over this one rewrites the legacy value but
  // not the version marker, so a legacy prefix names the last writer.
  const savedBy = legacyVersion ?? kv.getString(DURABLE_CACHE_APP_VERSION_KEY);
  let prepared = true;
  try {
    if (stored !== DURABLE_CACHE_BUSTER && legacyVersion === undefined) {
      db.delete(queryCache).run();
    } else if (savedBy !== appVersion) {
      keepAcrossAppUpdate(db);
    }
  } catch (err) {
    prepared = false;
    console.warn("[queryClient] preparing saved queries failed (continuing)", {
      stored,
      savedBy,
      appVersion,
      err,
    });
  }
  // A failed wipe still records the buster, as it always has. A failed
  // update keeps a legacy value, so the next attempt still knows the last
  // writer, and leaves the version marker for the next launch to retry.
  if (
    stored !== DURABLE_CACHE_BUSTER &&
    (legacyVersion === undefined || prepared)
  )
    kv.set(DURABLE_CACHE_BUSTER_KEY, DURABLE_CACHE_BUSTER);
  if (prepared && kv.getString(DURABLE_CACHE_APP_VERSION_KEY) !== appVersion)
    kv.set(DURABLE_CACHE_APP_VERSION_KEY, appVersion);
}

/**
 * The server answers some queries for the requesting app version (the
 * `App-Version` header): `user.me`'s `newUpdates`, and song status for old
 * builds. After an update the saved rows still render, but none counts as
 * fresh: each is marked as fetched at the epoch, so an online launch
 * refetches it at once. The saved profile's `newUpdates` described the old
 * version, so it is reset; kept, the new version would show the old one's
 * update banner or force-update screen until a fetch replaced it, which
 * offline never happens.
 */
function keepAcrossAppUpdate(db: MobileDatabase) {
  const key = stableStringify(meQueryKey());
  db.transaction((tx) => {
    tx.update(queryCache)
      .set({ fetchedAt: new Date(0) })
      .run();
    const [me] = tx
      .select({ data: queryCache.data })
      .from(queryCache)
      .where(eq(queryCache.queryKey, key))
      .all();
    if (me?.data && typeof me.data === "object" && !Array.isArray(me.data))
      tx.update(queryCache)
        .set({ data: { ...me.data, newUpdates: { available: false } } })
        .where(eq(queryCache.queryKey, key))
        .run();
  });
}

export function createQueryClient({
  db,
  kv: mmkv,
  cache,
  appVersion,
  defaults,
}: {
  db: MobileDatabase;
  kv: DevicePorts["kv"];
  cache: DurableCache;
  appVersion: string;
  defaults?: DefaultOptions;
}) {
  const queryClient = new QueryClient({
    defaultOptions: {
      ...defaults,
      queries: {
        staleTime: 1000 * 60 * 5,
        networkMode: "online",
        ...retryOptions,
        ...defaults?.queries,
      },
      mutations: { ...retryOptions, ...defaults?.mutations },
    },
    queryCache: new QueryCache(),
  });

  try {
    prepareSavedQueries({ db, kv: mmkv, appVersion: appVersion || "" });
  } catch (err) {
    // MMKV read/write failed — log and continue. Buster is a best-effort
    // safety net, not load-bearing.
    console.warn("[queryClient] SQLite buster check failed", { err });
  }

  // Auth mirror: the Firebase user and the `user.auth` answer. They are
  // in-memory queries saved to MMKV, not to `query_cache`, so the buster
  // never touches them. Restoring them synchronously when the session opens
  // avoids a login-screen flash while Firebase and the auth endpoint resolve
  // on a cold start, and `createAppRuntime` reads the restored user as the
  // previous identity on a device that has no `data.lastIdentity` yet.
  const AUTH_MIRROR_QUERIES: Record<string, string> = {
    [AUTH_QUERY_KEY]: "persisted-auth-query",
    [USER_EXISTS_IN_DB_QUERY_KEY]: "persisted-user-exists-in-db",
  };

  /** Clear the auth mirror. The runtime calls this when it ends the account
   *  session, so a signed-out or replaced account is never restored. */
  const clearAuthMirror = () => {
    for (const mmkvKey of Object.values(AUTH_MIRROR_QUERIES)) {
      mmkv.remove(mmkvKey);
    }
  };

  // Durable infinite-query persister: mirrors successful `state.data` for any
  // query opted in via `meta.durable: true` to the SQLite query_cache table.
  // See `utils/durableCache/createInfiniteQueryCache.ts` for the full filter
  // chain. Each session releases its registration on disposal.
  const stopPersister = cache.setupDurableInfiniteQueryPersister(queryClient);

  // Save the auth mirror to MMKV whenever it changes. Clear on logout (null
  // data) so stale auth is never restored.
  const stopMirror = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== "updated" || event.query.state.status !== "success")
      return;
    const key = event.query.queryKey[0] as string;
    const mmkvKey = AUTH_MIRROR_QUERIES[key];
    if (!mmkvKey) return;

    if (event.query.state.data) {
      mmkv.set(mmkvKey, JSON.stringify(event.query.state.data));
    } else {
      mmkv.remove(mmkvKey);
    }
  });

  // Restore the auth mirror synchronously when the session opens. Use
  // === undefined (not falsy) so we don't overwrite an explicit `null`
  // (logged-out state).
  for (const [queryKey, mmkvKey] of Object.entries(AUTH_MIRROR_QUERIES)) {
    if (queryClient.getQueryData([queryKey]) === undefined) {
      const saved = mmkv.getString(mmkvKey);
      if (saved) {
        try {
          queryClient.setQueryData([queryKey], JSON.parse(saved));
          console.log(
            `QueryClient: Restored ${queryKey} from the MMKV auth mirror`,
          );
        } catch {}
      }
    }
  }

  return {
    queryClient,
    clearAuthMirror,
    dispose() {
      stopMirror();
      stopPersister();
      // A removed query or mutation still schedules garbage collection when
      // its in-flight request settles or its screen unmounts, both of which
      // happen after this clear. At the hook's gcTime (up to an hour) that
      // timer keeps the old account's data in memory; at zero it lets go at
      // once. Clearing does not cancel a mutation's running timer, so
      // destroy cancels those now.
      for (const entry of [
        ...queryClient.getQueryCache().getAll(),
        ...queryClient.getMutationCache().getAll(),
      ]) {
        entry.gcTime = 0;
        entry.destroy();
      }
      queryClient.clear();
    },
  };
}
